import EventBus from 'eventing-bus';
import Menu from '@mui/material/Menu';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';
import CircularProgress from '@mui/material/CircularProgress';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import './index.css';
import LeadsFilters from './LeadsFilters';
import TitleRoute from '../../components/titleRoute';
import { setAssistant } from '../../store/Assistant/index';
import { searchLeads, revealLeads, getLeadsCredits, setLeadsFilters } from '../../store/Leads/index';

const LeadsPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const aiFilterTimer = useRef(null);

  const { records, revealedData, revealingIds, inCrmMap, loading, revealLoading, pagination, filters, creditsBalance, error } = useSelector(s => s['Leads']);

  const [ui, setUi] = useState({ selectedRows: [], clearRows: false, actionAnchor: null, actionRow: null });

  // On mount: read filters from URL and run search if any are present
  useEffect(() => {
    dispatch(getLeadsCredits());
    const params = new URLSearchParams(location['search']);
    const urlFilters = {};
    ['q', 'industry', 'country', 'city', 'state', 'department', 'employeeCount'].forEach(k => { if (params.get(k)) urlFilters[k] = params.get(k); });
    ['hasEmail', 'hasPhone', 'hasLinkedin'].forEach(k => { if (params.get(k) === 'true') urlFilters[k] = true; });
    if (Object.keys(urlFilters).length) {
      dispatch(setLeadsFilters(urlFilters));
      dispatch(searchLeads({ ...urlFilters, page: 1 }));
    }
  }, []);

  useEffect(() => {
    const unsub = EventBus.on('leads:setFilters', (aiFilters) => {
      clearTimeout(aiFilterTimer.current);
      aiFilterTimer.current = setTimeout(() => {
        dispatch(setLeadsFilters(aiFilters));
        dispatch(searchLeads({ ...aiFilters, page: 1 }));
      }, 100);
    });
    return () => { unsub(); clearTimeout(aiFilterTimer.current); };
  }, []);

  const pushFiltersToUrl = (activeFilters) => {
    const params = new URLSearchParams();
    Object.entries(activeFilters).forEach(([k, v]) => { if (v !== '' && v !== false && v != null) params.set(k, v); });
    history.replace({ search: params.toString() });
  };

  const handleSearch = useCallback((page = 1) => {
    pushFiltersToUrl(filters);
    dispatch(searchLeads({ ...filters, page, limit: pagination['limit'] }));
  }, [filters, pagination]);

  const handleFilterChange = (patch) => dispatch(setLeadsFilters(patch));

  // Only update state — search fires on Apply
  const handleSearchInput = (e) => dispatch(setLeadsFilters({ q: e['target']['value'] }));

  const handleClearFilters = () => {
    dispatch(setLeadsFilters({ q: '', industry: '', country: '', city: '', state: '', department: '', employeeCount: '', hasEmail: true, hasPhone: true, hasLinkedin: true }));
    history.replace({ search: '' });
    dispatch(searchLeads({ page: 1, limit: pagination['limit'] }));
  };

  const openActionMenu = (e, row) => setUi(prev => ({ ...prev, actionAnchor: e['currentTarget'], actionRow: row }));
  const closeActionMenu = () => setUi(prev => ({ ...prev, actionAnchor: null, actionRow: null }));

  const handleRevealRow = (row, fields) => {
    const prospectId = row['id'] || row['_id'];
    dispatch(revealLeads({ prospectIds: [prospectId], fields }));
  };

  const handleRowClick = (row) => {
    const id = row['id'] || row['_id'];
    if (revealedData[id] || revealingIds[id]) return;
    // Reason: CRM sync happens server-side inside /reveal — no onSuccess save needed here
    dispatch(revealLeads({ prospectIds: [id], fields: ['email', 'phone', 'linkedin'] }));
  };

  const handleBulkReveal = () => {
    const ids = ui['selectedRows'].map(r => r['id'] || r['_id']);
    dispatch(revealLeads({ prospectIds: ids, fields: ['email', 'phone', 'linkedin'] }));
  };

  const handleAskMilo = () => {
    dispatch(setAssistant(true));
    setTimeout(() => EventBus.publish('assistant:prefill', { message: '@setLeadsFilters Find me CEOs of SaaS companies in the US' }), 150);
  };

  const getColumns = () => COLUMNS(revealedData, revealingIds, inCrmMap, handleRevealRow, openActionMenu);

  // Reason: keep formatting in sync with the sidebar "Free Credits" widget — both show raw creditsBalance (1 credit ≈ $0.01)
  const creditsDisplay = Math.round(parseFloat(creditsBalance) || 0).toLocaleString('en-US');

  return (
    <div className="leads-page">
      <div className="leads-header">
        <div className="leads-header-left">
          <TitleRoute />
          <p className="records-title">
            {pagination['total'] > 0 ? `${pagination['total'].toLocaleString()} records` : 'Search 16M+ prospects'}
          </p>
        </div>
        <div className="leads-header-right">
          <div className="leads-credits-widget">
            <i className="fa fa-coins leads-credits-icon" />
            <span className="leads-credits-label">Credits:</span>
            {creditsDisplay}
          </div>
          <button className="btn-style-new leads-action-btn" onClick={handleBulkReveal} disabled={!ui['selectedRows'].length || revealLoading}>
            {revealLoading ? <i className="fa fa-spinner fa-spin icon" /> : <i className="fa fa-unlock-alt icon" />}
            Reveal ({ui['selectedRows'].length})
          </button>
        </div>
      </div>

      <div className="leads-body">
        <LeadsFilters filters={filters} onChange={handleFilterChange} onApply={() => handleSearch(1)} onClear={handleClearFilters} onSearchChange={handleSearchInput} />

        <div className="leads-table-card">
          <div className="leads-table-wrapper">
            <DataTable
              columns={getColumns()}
              data={records}
              selectableRows
              onSelectedRowsChange={({ selectedRows }) => setUi(prev => ({ ...prev, selectedRows }))}
              clearSelectedRows={ui['clearRows']}
              pagination
              paginationServer
              paginationTotalRows={pagination['total']}
              paginationPerPage={pagination['limit']}
              onChangePage={(p) => handleSearch(p)}
              onRowClicked={handleRowClick}
              progressPending={loading}
              progressComponent={<CircularProgress size={32} sx={{ my: 4, color: 'var(--primary-color)' }} />}
              className="leads-table"
              fixedHeader
              fixedHeaderScrollHeight="100%"
              noDataComponent={
                error ? (
                  <div className="leads-empty-state">
                    <i className="fa fa-exclamation-circle leads-empty-icon leads-empty-icon--error" />
                    <div className="leads-empty-title">Search failed</div>
                    <div className="leads-empty-sub">{error}</div>
                    <button className="btn-style-border leads-empty-milo-btn" onClick={() => handleSearch(1)}>
                      <i className="fa fa-refresh icon" /> Try Again
                    </button>
                  </div>
                ) : (
                  <div className="leads-empty-state">
                    <i className="fa fa-users leads-empty-icon" />
                    <div className="leads-empty-title">Search 16M+ prospects</div>
                    <div className="leads-empty-sub">Use the filters on the left and click Apply, or let Milo set them for you.</div>
                    <button className="btn-style-new leads-empty-milo-btn" onClick={handleAskMilo}>
                      <i className="fa fa-magic icon" /> Ask Milo to find prospects
                    </button>
                  </div>
                )
              }
            />
          </div>
        </div>
      </div>

      {/* Row action menu */}
      <Menu anchorEl={ui['actionAnchor']} open={Boolean(ui['actionAnchor'])} onClose={closeActionMenu}>
        <MenuItem className="leads-actions-menu-item" onClick={() => { handleRevealRow(ui['actionRow'], ['email', 'phone', 'linkedin']); closeActionMenu(); }}>Reveal All Fields</MenuItem>
      </Menu>


    </div>
  );
};

export default LeadsPage;

const COLUMNS = (revealedData, revealingIds, inCrmMap, onReveal, onMenuOpen) => [
  {
    name: 'Name',
    minWidth: '130px',
    grow: 2,
    cell: row => {
      const id = row['id'] || row['_id'];
      return (
        <div className="leads-name-cell">
          {`${row['firstName'] || ''} ${row['lastName'] || ''}`.trim() || '—'}
          {inCrmMap[id] && (
            <Tooltip title="Added to Contacts" placement="top" arrow>
              <i className="fa fa-check-circle leads-in-crm-icon" aria-label="Added to Contacts" />
            </Tooltip>
          )}
        </div>
      );
    },
  },
  { name: 'Job Title', selector: row => row['title'] || '—', minWidth: '100px', grow: 1.5 },
  { name: 'Company', selector: row => row['company'] || '—', minWidth: '100px', grow: 1.5 },
  { name: 'Location', selector: row => [row['city'], row['country']].filter(Boolean).join(', ') || '—', minWidth: '100px', grow: 1.5 },
  { name: 'Industry', selector: row => row['industry'] || '—', minWidth: '90px', grow: 1 },
  {
    name: 'Email',
    minWidth: '140px',
    grow: 2,
    cell: row => {
      const id = row['id'] || row['_id'];
      const revealed = revealedData[id];
      const loading = revealingIds[id];
      if (revealed) return revealed['email']
        ? <span className="leads-revealed-value">{revealed['email']}</span>
        : <span className="leads-lock-cell"><i className="fa fa-ban leads-lock-icon" /> N/A</span>;
      if (!row['hasEmail']) return <span className="leads-lock-cell"><i className="fa fa-ban leads-lock-icon" /> N/A</span>;
      if (loading) return <span className="leads-lock-cell"><i className="fa fa-spinner fa-spin leads-lock-icon" /></span>;
      return (
        <div className="leads-lock-cell">
          <i className="fa fa-lock leads-lock-icon" />
          <button className="leads-reveal-btn" onClick={() => onReveal(row, ['email', 'phone', 'linkedin'])}>1 Credit</button>
        </div>
      );
    },
  },
  {
    name: 'Phone',
    minWidth: '130px',
    grow: 1.5,
    cell: row => {
      const id = row['id'] || row['_id'];
      const revealed = revealedData[id];
      const loading = revealingIds[id];
      if (revealed) return revealed['phone']
        ? <span className="leads-revealed-value">{revealed['phone']}</span>
        : <span className="leads-lock-cell"><i className="fa fa-ban leads-lock-icon" /> N/A</span>;
      if (!row['hasPhone']) return <span className="leads-lock-cell"><i className="fa fa-ban leads-lock-icon" /> N/A</span>;
      if (loading) return <span className="leads-lock-cell"><i className="fa fa-spinner fa-spin leads-lock-icon" /></span>;
      return (
        <div className="leads-lock-cell">
          <i className="fa fa-lock leads-lock-icon" />
          <button className="leads-reveal-btn" onClick={() => onReveal(row, ['email', 'phone', 'linkedin'])}>1 Credit</button>
        </div>
      );
    },
  },
  {
    name: 'LinkedIn',
    minWidth: '90px',
    grow: 1,
    cell: row => {
      const id = row['id'] || row['_id'];
      const revealed = revealedData[id];
      const loading = revealingIds[id];
      if (revealed) return revealed['linkedinUrl']
        ? <a className="leads-linkedin-link" href={revealed['linkedinUrl']} target="_blank" rel="noreferrer"><i className="fab fa-linkedin" /><i className="fa fa-external-link leads-linkedin-open-icon" /></a>
        : <span className="leads-lock-cell"><i className="fa fa-ban leads-lock-icon" /> N/A</span>;
      if (!row['hasLinkedin']) return <span className="leads-lock-cell"><i className="fa fa-ban leads-lock-icon" /> N/A</span>;
      if (loading) return <span className="leads-lock-cell"><i className="fa fa-spinner fa-spin leads-lock-icon" /></span>;
      return (
        <div className="leads-lock-cell">
          <i className="fa fa-lock leads-lock-icon" />
          <button className="leads-reveal-btn" onClick={() => onReveal(row, ['email', 'phone', 'linkedin'])}>1 Credit</button>
        </div>
      );
    },
  },
  {
    name: '',
    width: '40px',
    cell: row => (
      <Tooltip title="Actions">
        <IconButton size="small" onClick={(e) => onMenuOpen(e, row)}>
          <i className="fa fa-ellipsis-v" style={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    ),
    right: true,
  },
];
